import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import contractService from '../services/contractService';
import './VaultSourceList.css';
import './ContractsList.css';

const APPLICABLE_OPTIONS = ['PARTNER', 'CHAIN', 'HOTEL'];

const EMPTY = {
  name: '', version: '', description: '', content: '',
  status: 'draft', applicable_to: [], is_active: true, is_default: false,
};

const parseApplicableTo = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  return val.split(',').filter(Boolean);
};

const renderMarkdownPdf = (contract) => {
  const escape = s => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  let html = escape(contract.content)
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^---+$/gm, '<hr>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
    .replace(/\n{2,}/g, '</p><p>')
    .replace(/\n/g, '<br>');

  const w = window.open('', '_blank');
  w.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${escape(contract.name)} v${escape(contract.version)}</title>
  <style>
    body { font-family: Georgia, serif; max-width: 800px; margin: 40px auto; font-size: 14px; line-height: 1.7; color: #1a1a1a; }
    h1 { font-size: 24px; border-bottom: 2px solid #333; padding-bottom: 8px; margin-top: 32px; }
    h2 { font-size: 18px; margin-top: 28px; }
    h3 { font-size: 15px; margin-top: 20px; }
    hr { border: none; border-top: 1px solid #ccc; margin: 24px 0; }
    li { margin: 4px 0; }
    table { width: 100%; border-collapse: collapse; margin: 16px 0; }
    th, td { border: 1px solid #ccc; padding: 8px 12px; text-align: left; }
    th { background: #f5f5f5; font-weight: 600; }
    .doc-header { margin-bottom: 32px; }
    .doc-title { font-size: 28px; font-weight: bold; }
    .doc-meta { color: #666; font-size: 13px; margin-top: 8px; }
    @media print { body { margin: 20mm; } .no-print { display: none; } }
  </style>
</head>
<body>
  <div class="doc-header">
    <div class="doc-title">${escape(contract.name)}</div>
    <div class="doc-meta">Version ${escape(contract.version)} &nbsp;|&nbsp; Status: ${escape(contract.status)}</div>
  </div>
  <p>${html}</p>
  <script>window.onload = function() { window.print(); }</script>
</body>
</html>`);
  w.document.close();
};

const ContractForm = () => {
  const navigate  = useNavigate();
  const { id }    = useParams();
  const isNew     = !id;

  const [form, setForm]       = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (isNew) return;
    contractService.getContract(id)
      .then(res => {
        const c = res.contract;
        setForm({
          ...c,
          applicable_to: parseApplicableTo(c.applicable_to),
          is_active:  c.is_active  !== false && c.is_active  !== 0,
          is_default: c.is_default === true  || c.is_default === 1,
        });
      })
      .catch(err => setError(err.response?.data?.message || 'Failed to load contract'))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleCheckbox = e => setForm(f => ({ ...f, [e.target.name]: e.target.checked }));

  const handleApplicableTo = (option) => {
    setForm(f => {
      const current = f.applicable_to;
      const next = current.includes(option)
        ? current.filter(v => v !== option)
        : [...current, option];
      return { ...f, applicable_to: next };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.version || !form.content) {
      setError('Name, version and content are required');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const payload = {
        ...form,
        applicable_to: form.applicable_to.length ? form.applicable_to.join(',') : null,
      };
      if (isNew) {
        await contractService.createContract(payload);
      } else {
        await contractService.updateContract(id, payload);
      }
      navigate('/admin/contracts');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save contract');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="vaultsource-list-container">
      <div className="header">
        <h1>{isNew ? 'New Contract' : 'Edit Contract'}</h1>
      </div>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit} className="form-card">
        <div className="form-row">
          <div className="form-group" style={{ flex: 2 }}>
            <label>Name *</label>
            <input name="name" value={form.name} onChange={handleChange} placeholder="MSP Partner Agreement" required />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label>Version *</label>
            <input name="version" value={form.version} onChange={handleChange} placeholder="1.0" required />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label>Status</label>
            <select name="status" value={form.status} onChange={handleChange}>
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>

        <div className="form-row" style={{ alignItems: 'flex-start', gap: '24px' }}>
          <div className="form-group" style={{ flex: 1 }}>
            <label>Applicable To</label>
            <div className="contracts-checkboxes">
              {APPLICABLE_OPTIONS.map(opt => (
                <label key={opt} className="contracts-checkbox-label">
                  <input
                    type="checkbox"
                    checked={form.applicable_to.includes(opt)}
                    onChange={() => handleApplicableTo(opt)}
                  />
                  {opt}
                </label>
              ))}
            </div>
          </div>

          <div className="form-group" style={{ flex: 1 }}>
            <label>Flags</label>
            <div className="contracts-checkboxes">
              <label className="contracts-checkbox-label">
                <input type="checkbox" name="is_active" checked={!!form.is_active} onChange={handleCheckbox} />
                Active
              </label>
              <label className="contracts-checkbox-label">
                <input type="checkbox" name="is_default" checked={!!form.is_default} onChange={handleCheckbox} />
                Default version
              </label>
            </div>
          </div>
        </div>

        <div className="form-group">
          <label>Description</label>
          <input name="description" value={form.description || ''} onChange={handleChange} placeholder="Short description of this contract" />
        </div>

        <div className="form-group">
          <label>Content (Markdown) *</label>
          <textarea
            name="content"
            value={form.content}
            onChange={handleChange}
            rows={30}
            style={{ fontFamily: 'monospace', fontSize: '13px', resize: 'vertical' }}
            placeholder="# Contract Title&#10;&#10;Enter your contract content in Markdown format..."
            required
          />
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : (isNew ? 'Create Contract' : 'Save Changes')}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => renderMarkdownPdf(form)}
            disabled={!form.content}
          >
            Preview / Download PDF
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin/contracts')}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContractForm;
